import * as React from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { LucideIcon } from './LucideIcon';
import type { DateTime as Interface } from '../../design/components';
import {
  type DatePickerVariant,
  getTimezoneAbbr,
  utcToLocal,
  localToUtc,
  formatDisplay,
  parseToDate,
  parseDateRange,
  formatDateISO,
  MONTH_NAMES_SHORT,
  getDecadeStart,
  buildDisabledDays,
  getStartMonth,
  getEndMonth,
  isMonthInRange,
  isYearInRange,
  minMaxToLocalTime,
} from '../shared/datetime';

export const DateTime: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder,
  hidden,
  inline,
  readonly,
  disabled,
  required,
  error,
  variant = 'date',
  range,
  min,
  max,
}) => {
  const [value, setValue] = state || ['', () => {}];
  const v = variant as DatePickerVariant;
  const strValue = typeof value === 'string' ? value : '';
  const showTz = v === 'date-time' || v === 'time';
  const minStr = min != null ? String(min) : undefined;
  const maxStr = max != null ? String(max) : undefined;

  if (inline) {
    return (
      <span className={cn(hidden && 'hidden')}>
        {formatDisplay(strValue, v, range)}
      </span>
    );
  }

  // Range mode for time: two native inputs
  if (range && v === 'time') {
    const [startStr, endStr] = strValue.split('/');
    return (
      <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
        {name && (
          <Label className={cn(error && 'text-destructive')}>
            {name}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <div className="flex items-center gap-2">
          <Input
            type="time"
            value={utcToLocal(startStr || '', v)}
            onChange={(e) => {
              const start = localToUtc(e.target.value, v);
              setValue(endStr ? `${start}/${endStr}` : start);
            }}
            min={minMaxToLocalTime(min)}
            max={minMaxToLocalTime(max)}
            disabled={disabled}
            readOnly={readonly}
            className={cn('flex-1', error && 'border-destructive', readonly && 'bg-muted')}
          />
          <span className="text-muted-foreground">–</span>
          <Input
            type="time"
            value={utcToLocal(endStr || '', v)}
            onChange={(e) => {
              const end = localToUtc(e.target.value, v);
              setValue(startStr ? `${startStr}/${end}` : end);
            }}
            min={minMaxToLocalTime(min)}
            max={minMaxToLocalTime(max)}
            disabled={disabled}
            readOnly={readonly}
            className={cn('flex-1', error && 'border-destructive', readonly && 'bg-muted')}
          />
          <span className="text-muted-foreground text-sm">{getTimezoneAbbr()}</span>
        </div>
        {(error || description) && (
          <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
            {error || description}
          </p>
        )}
      </div>
    );
  }

  // Range mode for month/year: two picker popovers
  if (range && (v === 'month' || v === 'year')) {
    const [startStr, endStr] = strValue.split('/');
    const Picker = v === 'month' ? MonthPicker : YearPicker;
    return (
      <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
        {name && (
          <Label className={cn(error && 'text-destructive')}>
            {name}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Picker
              value={startStr || ''}
              onChange={(val) => setValue(endStr ? `${val}/${endStr}` : val)}
              disabled={disabled}
              readonly={readonly}
              error={!!error}
              placeholder="Start"
              min={minStr}
              max={maxStr}
            />
          </div>
          <span className="text-muted-foreground">–</span>
          <div className="flex-1">
            <Picker
              value={endStr || ''}
              onChange={(val) => setValue(startStr ? `${startStr}/${val}` : val)}
              disabled={disabled}
              readonly={readonly}
              error={!!error}
              placeholder="End"
              min={minStr}
              max={maxStr}
            />
          </div>
        </div>
        {(error || description) && (
          <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
            {error || description}
          </p>
        )}
      </div>
    );
  }

  // Time single: native input
  if (v === 'time' && !range) {
    return (
      <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
        {name && (
          <Label htmlFor={name} className={cn(error && 'text-destructive')}>
            {name}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <div className="flex items-center gap-2">
          <Input
            type="time"
            id={name}
            name={name}
            value={utcToLocal(strValue, v)}
            onChange={(e) => setValue(localToUtc(e.target.value, v))}
            min={minMaxToLocalTime(min)}
            max={minMaxToLocalTime(max)}
            disabled={disabled}
            readOnly={readonly}
            required={required}
            className={cn(error && 'border-destructive', readonly && 'bg-muted')}
          />
          {showTz && <span className="text-muted-foreground text-sm">{getTimezoneAbbr()}</span>}
        </div>
        {(error || description) && (
          <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
            {error || description}
          </p>
        )}
      </div>
    );
  }

  // Month single: popover with month grid
  if (v === 'month' && !range) {
    return (
      <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
        {name && (
          <Label className={cn(error && 'text-destructive')}>
            {name}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <MonthPicker value={strValue} onChange={setValue} disabled={disabled} readonly={readonly} error={!!error} placeholder={placeholder} min={minStr} max={maxStr} />
        {(error || description) && (
          <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
            {error || description}
          </p>
        )}
      </div>
    );
  }

  // Year single: popover with year grid
  if (v === 'year' && !range) {
    return (
      <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
        {name && (
          <Label className={cn(error && 'text-destructive')}>
            {name}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <YearPicker value={strValue} onChange={setValue} disabled={disabled} readonly={readonly} error={!!error} placeholder={placeholder} min={minStr} max={maxStr} />
        {(error || description) && (
          <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
            {error || description}
          </p>
        )}
      </div>
    );
  }

  // Calendar-based: date, date-time (single or range)
  return (
    <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
      {name && (
        <Label className={cn(error && 'text-destructive')}>
          {name}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <CalendarPicker
        variant={v}
        range={range}
        value={strValue}
        onChange={setValue}
        placeholder={placeholder}
        disabled={disabled}
        readonly={readonly}
        error={!!error}
        showTz={showTz}
        min={min}
        max={max}
      />
      {(error || description) && (
        <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
          {error || description}
        </p>
      )}
    </div>
  );
};

/** Calendar popover for date and date-time variants */
function CalendarPicker({
  variant, range: isRange, value, onChange, placeholder, disabled, readonly, error, showTz, min, max,
}: {
  variant: DatePickerVariant; range?: boolean; value: string; onChange: (v: string) => void;
  placeholder?: string; disabled?: boolean; readonly?: boolean; error?: boolean; showTz?: boolean;
  min?: string | Date; max?: string | Date;
}) {
  const [open, setOpen] = React.useState(false);
  const displayText = value ? formatDisplay(value, variant, isRange) : (placeholder || 'Pick a date');
  const disabledDays = buildDisabledDays(min, max);
  const startMonth = getStartMonth(min);
  const endMonth = getEndMonth(max);

  return (
    <Popover open={open} onOpenChange={(o) => { if (!readonly && !disabled) setOpen(o); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal h-9',
            !value && 'text-muted-foreground',
            error && 'border-destructive',
            disabled && 'pointer-events-none opacity-50',
            readonly && 'bg-muted'
          )}
        >
          <LucideIcon name="Calendar" size="small" />
          <span className="flex-1">{displayText}</span>
          {value && !readonly && !disabled && (
            <span role="button" className="ml-auto hover:text-foreground text-muted-foreground"
              onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }}>
              <LucideIcon name="X" size="small" />
            </span>
          )}
          {showTz && value && <span className="text-muted-foreground text-xs ml-1">{getTimezoneAbbr()}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {variant === 'date' && !isRange && (
          <Calendar
            mode="single"
            selected={parseToDate(value, variant)}
            onSelect={(day) => { if (day) { onChange(formatDateISO(day)); setOpen(false); } }}
            defaultMonth={parseToDate(value, variant)}
            disabled={disabledDays}
            startMonth={startMonth}
            endMonth={endMonth}
          />
        )}
        {variant === 'date' && isRange && (
          <Calendar
            mode="range"
            selected={parseDateRange(value) || undefined}
            onSelect={(r) => {
              if (r?.from && r?.to) { onChange(`${formatDateISO(r.from)}/${formatDateISO(r.to)}`); }
            }}
            numberOfMonths={2}
            defaultMonth={parseDateRange(value)?.from}
            disabled={disabledDays}
            startMonth={startMonth}
            endMonth={endMonth}
          />
        )}
        {variant === 'date-time' && !isRange && (
          <DateTimeCalendar value={value} onChange={(val) => { onChange(val); setOpen(false); }} min={min} max={max} />
        )}
        {variant === 'date-time' && isRange && (
          <DateTimeRangeCalendar value={value} onChange={onChange} min={min} max={max} />
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Calendar + time input for single date-time */
function DateTimeCalendar({ value, onChange, min, max }: { value: string; onChange: (v: string) => void; min?: string | Date; max?: string | Date }) {
  const date = value ? new Date(value) : undefined;
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(date);
  const localTime = value ? utcToLocal(value, 'date-time').split('T')[1] || '12:00' : '12:00';
  const [time, setTime] = React.useState(localTime);
  const disabledDays = buildDisabledDays(min, max);

  const commit = (d: Date | undefined, t: string) => {
    if (!d) return;
    onChange(localToUtc(formatDateISO(d) + 'T' + t, 'date-time'));
  };

  return (
    <div>
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={(day) => { setSelectedDate(day || undefined); if (day) commit(day, time); }}
        defaultMonth={selectedDate}
        disabled={disabledDays}
        startMonth={getStartMonth(min)}
        endMonth={getEndMonth(max)}
      />
      <div className="border-t px-3 py-2 flex items-center gap-2">
        <Input type="time" value={time}
          min={minMaxToLocalTime(min)}
          max={minMaxToLocalTime(max)}
          onChange={(e) => { setTime(e.target.value); commit(selectedDate, e.target.value); }}
          className="flex-1 h-8" />
        <span className="text-muted-foreground text-xs">{getTimezoneAbbr()}</span>
      </div>
    </div>
  );
}

/** Calendar range + two time inputs for date-time range */
function DateTimeRangeCalendar({ value, onChange, min, max }: { value: string; onChange: (v: string) => void; min?: string | Date; max?: string | Date }) {
  const [startStr, endStr] = value.split('/');
  const startDate = startStr ? new Date(startStr) : undefined;
  const endDate = endStr ? new Date(endStr) : undefined;
  const [range, setRange] = React.useState<{ from?: Date; to?: Date }>({
    from: startDate && !isNaN(startDate.getTime()) ? startDate : undefined,
    to: endDate && !isNaN(endDate.getTime()) ? endDate : undefined,
  });
  const startTime = startStr ? utcToLocal(startStr, 'date-time').split('T')[1] || '12:00' : '12:00';
  const endTime = endStr ? utcToLocal(endStr, 'date-time').split('T')[1] || '12:00' : '12:00';
  const [times, setTimes] = React.useState([startTime, endTime]);
  const disabledDays = buildDisabledDays(min, max);

  const commit = (r: { from?: Date; to?: Date }, t: string[]) => {
    if (!r.from || !r.to) return;
    const s = localToUtc(formatDateISO(r.from) + 'T' + t[0], 'date-time');
    const e = localToUtc(formatDateISO(r.to) + 'T' + t[1], 'date-time');
    onChange(`${s}/${e}`);
  };

  return (
    <div>
      <Calendar
        mode="range"
        selected={range.from && range.to ? { from: range.from, to: range.to } : undefined}
        onSelect={(r) => {
          const newRange = { from: r?.from || undefined, to: r?.to || undefined };
          setRange(newRange);
          if (newRange.from && newRange.to) commit(newRange, times);
        }}
        defaultMonth={range.from}
        disabled={disabledDays}
        startMonth={getStartMonth(min)}
        endMonth={getEndMonth(max)}
      />
      <div className="border-t px-3 py-2 flex items-center gap-2">
        <Input type="time" value={times[0]}
          onChange={(e) => { const t = [e.target.value, times[1]]; setTimes(t); commit(range, t); }}
          className="flex-1 h-8" />
        <span className="text-muted-foreground">–</span>
        <Input type="time" value={times[1]}
          onChange={(e) => { const t = [times[0], e.target.value]; setTimes(t); commit(range, t); }}
          className="flex-1 h-8" />
        <span className="text-muted-foreground text-xs">{getTimezoneAbbr()}</span>
      </div>
    </div>
  );
}

/** 3×4 month grid picker in a Popover */
function MonthPicker({ value, onChange, disabled, readonly, error, placeholder, min, max }: {
  value: string; onChange: (v: string) => void;
  disabled?: boolean; readonly?: boolean; error?: boolean; placeholder?: string;
  min?: string; max?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [y] = value ? value.split('-').map(Number) : [new Date().getFullYear()];
  const [year, setYear] = React.useState(y);
  const displayText = value ? formatDisplay(value, 'month') : (placeholder || 'Pick a month');

  return (
    <Popover open={open} onOpenChange={(o) => { if (!readonly && !disabled) setOpen(o); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn(
          'w-full justify-start text-left font-normal h-9',
          !value && 'text-muted-foreground',
          error && 'border-destructive',
          disabled && 'pointer-events-none opacity-50',
          readonly && 'bg-muted'
        )}>
          <LucideIcon name="Calendar" size="small" />
          <span className="flex-1">{displayText}</span>
          {value && !readonly && !disabled && (
            <span role="button" className="ml-auto hover:text-foreground text-muted-foreground"
              onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }}>
              <LucideIcon name="X" size="small" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="flex items-center justify-between mb-3">
          <Button variant="outline" size="icon-xs" onClick={() => setYear(year - 1)}
            disabled={min ? year - 1 < Number(min.slice(0, 4)) : false}>
            <LucideIcon name="ChevronLeft" size="small" />
          </Button>
          <span className="text-sm font-medium">{year}</span>
          <Button variant="outline" size="icon-xs" onClick={() => setYear(year + 1)}
            disabled={max ? year + 1 > Number(max.slice(0, 4)) : false}>
            <LucideIcon name="ChevronRight" size="small" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {MONTH_NAMES_SHORT.map((name, i) => {
            const monthVal = `${year}-${String(i + 1).padStart(2, '0')}`;
            const isSelected = monthVal === value;
            const inRange = isMonthInRange(monthVal, min, max);
            return (
              <Button
                key={i}
                variant={isSelected ? 'default' : 'ghost'}
                size="sm"
                className="h-8"
                disabled={!inRange}
                onClick={() => { onChange(monthVal); setOpen(false); }}
              >
                {name}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** 3×4 year grid picker in a Popover */
function YearPicker({ value, onChange, disabled, readonly, error, placeholder, min, max }: {
  value: string; onChange: (v: string) => void;
  disabled?: boolean; readonly?: boolean; error?: boolean; placeholder?: string;
  min?: string; max?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const currentYear = value ? Number(value) : new Date().getFullYear();
  const [decadeStart, setDecadeStart] = React.useState(getDecadeStart(currentYear));
  const displayText = value || (placeholder || 'Pick a year');

  return (
    <Popover open={open} onOpenChange={(o) => { if (!readonly && !disabled) setOpen(o); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn(
          'w-full justify-start text-left font-normal h-9',
          !value && 'text-muted-foreground',
          error && 'border-destructive',
          disabled && 'pointer-events-none opacity-50',
          readonly && 'bg-muted'
        )}>
          <LucideIcon name="Calendar" size="small" />
          <span className="flex-1">{displayText}</span>
          {value && !readonly && !disabled && (
            <span role="button" className="ml-auto hover:text-foreground text-muted-foreground"
              onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }}>
              <LucideIcon name="X" size="small" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="flex items-center justify-between mb-3">
          <Button variant="outline" size="icon-xs" onClick={() => setDecadeStart(decadeStart - 12)}
            disabled={min ? decadeStart - 1 < Number(min.slice(0, 4)) : false}>
            <LucideIcon name="ChevronLeft" size="small" />
          </Button>
          <span className="text-sm font-medium">{decadeStart}–{decadeStart + 11}</span>
          <Button variant="outline" size="icon-xs" onClick={() => setDecadeStart(decadeStart + 12)}
            disabled={max ? decadeStart + 12 > Number(max.slice(0, 4)) : false}>
            <LucideIcon name="ChevronRight" size="small" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 12 }, (_, i) => {
            const yr = String(decadeStart + i);
            const isSelected = yr === value;
            const inRange = isYearInRange(yr, min, max);
            return (
              <Button
                key={yr}
                variant={isSelected ? 'default' : 'ghost'}
                size="sm"
                className="h-8"
                disabled={!inRange}
                onClick={() => { onChange(yr); setOpen(false); }}
              >
                {yr}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
